import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { PRODUCTS } from "@/lib/products";
import { GALLERY } from "@/constants/testIds";
import { ArrowUpRight } from "lucide-react";

function Card({ product, index, onOpen }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(
    scrollYProgress,
    [0, 1],
    [index % 2 === 0 ? 50 : -30, index % 2 === 0 ? -50 : 30],
  );

  const handleMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const mx = ((e.clientX - rect.left) / rect.width) * 100;
    const my = ((e.clientY - rect.top) / rect.height) * 100;
    e.currentTarget.style.setProperty("--mx", `${mx}%`);
    e.currentTarget.style.setProperty("--my", `${my}%`);
  };

  return (
    <motion.article
      ref={ref}
      style={{ y }}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ delay: (index % 4) * 0.06, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`${GALLERY.cardPrefix}-${product.id}`}
      className={
        "group relative " +
        (index % 3 === 0 ? "md:mt-10" : index % 3 === 2 ? "md:mt-24" : "")
      }
    >
      <button
        onMouseMove={handleMove}
        onClick={() => onOpen(product)}
        data-testid={`${GALLERY.viewBtnPrefix}-${product.id}`}
        data-cursor="hover"
        className="tee-wrap block w-full aspect-[3/4] border border-[var(--ink)] text-left"
        aria-label={`Προβολή ${product.title}`}
      >
        <img
          src={product.image}
          alt={`Σχέδιο μπλουζάκι — ${product.title}. ${product.desc}`}
          loading="lazy"
          className="w-full h-full object-cover"
          onError={(e) => {
            e.currentTarget.style.background =
              "repeating-linear-gradient(45deg,#0A0A0A,#0A0A0A 6px,#ECECE8 6px,#ECECE8 12px)";
            e.currentTarget.style.opacity = "0.15";
          }}
        />
        <div className="spotlight" />
        <div className="absolute top-3 left-3 font-mono-caps text-[10px] bg-[var(--bg)] border border-[var(--ink)] px-2 py-1">
          {product.tag}
        </div>
        <div className="absolute bottom-3 right-3 font-display text-lg bg-[var(--ink)] text-[var(--bg)] px-2 py-1">
          {product.price}
        </div>
        <div className="absolute inset-0 flex items-end p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-t from-black/40 via-transparent to-transparent">
          <span className="btn-lime !py-2 !px-3 !text-xs">
            ΠΡΟΒΟΛΗ <ArrowUpRight className="w-3 h-3" />
          </span>
        </div>
      </button>
      <div className="mt-4 flex items-baseline justify-between">
        <h3 className="font-display text-xl md:text-2xl">{product.title}</h3>
        <span className="font-mono-caps text-[10px]">Νο. {String(index + 1).padStart(2, "0")}</span>
      </div>
    </motion.article>
  );
}

export default function Gallery({ onOpen }) {
  return (
    <section
      id="gallery"
      data-testid={GALLERY.root}
      className="relative px-4 md:px-8 lg:px-12 py-24 md:py-32 border-t border-[var(--ink)]"
    >
      <div className="flex items-baseline justify-between mb-10 md:mb-16">
        <span className="font-mono-caps text-xs">§ 03 — Η ΣΥΛΛΟΓΗ</span>
        <span className="font-mono-caps text-xs">10 ΣΧΕΔΙΑ · 5 ΧΡΩΜΑΤΑ</span>
      </div>

      <motion.h2
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="font-display text-5xl md:text-7xl lg:text-[9vw] leading-[0.9] mb-16 md:mb-24"
      >
        ΤΟ DROP.
      </motion.h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 md:gap-10">
        {PRODUCTS.map((p, i) => (
          <Card key={p.id} product={p} index={i} onOpen={onOpen} />
        ))}
      </div>
    </section>
  );
}
